import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import * as apiComms from '../apiComms';
import SideNavBar from '../components/SideNavBar';
import TopNavBar from '../components/TopNavBar';
import useIsMobile, { DESKTOP_CONTENT_MARGIN_LEFT, MOBILE_CONTENT_MARGIN_LEFT } from '../hooks/useIsMobile';

const formatDate = (epoch, readable) => {
  const value = epoch ?? readable;
  if (value == null || value === '') return 'Not recorded';
  const numeric = Number(value);
  const parsed = Number.isFinite(numeric) ? dayjs(numeric * 1000) : dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MMM/YYYY HH:mm') : 'Not recorded';
};

const DetailField = ({ label, value }) => (
  <div style={ styles.detailField }>
    <span style={ styles.fieldLabel }>{ label }</span>
    <span style={ styles.fieldValue }>{ value || 'Not recorded' }</span>
  </div>
);

const CouponDetails = ({ handleUpdate }) => {
  const { redemptionId } = useParams();
  const isMobile = useIsMobile();
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collectionHistory, setCollectionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [missingTransactionCount, setMissingTransactionCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setDetails(null);
    setError(null);
    setIsLoading(true);

    apiComms.getCouponRedemptionDetail(redemptionId).then((result) => {
      if (cancelled) return;
      if (result?.isErr || !result?.response?.coupon) {
        setError(result?.errMsg ?? 'Unable to load coupon.');
      }
      else {
        setDetails(result.response);
      }
    }).catch(() => {
      if (!cancelled) setError('Unable to load coupon.');
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [redemptionId]);

  useEffect(() => {
    let cancelled = false;
    setCollectionHistory([]);
    setHistoryError(null);
    setMissingTransactionCount(0);
    setHistoryLoading(true);

    apiComms.getCouponCollectionHistory(redemptionId).then((result) => {
      if (cancelled) return;
      if (result?.isErr || !Array.isArray(result?.response?.collectionHistory)) {
        setHistoryError(result?.errMsg ?? 'Unable to load collection history.');
      }
      else {
        setCollectionHistory(result.response.collectionHistory);
        setMissingTransactionCount(Number(result.response.missingTransactionCount ?? 0));
      }
    }).catch(() => {
      if (!cancelled) setHistoryError('Unable to load collection history.');
    }).finally(() => {
      if (!cancelled) setHistoryLoading(false);
    });

    return () => { cancelled = true; };
  }, [redemptionId]);

  const navigate = (route) => handleUpdate({ updateType: 'navigate', route });
  const coupon = details?.coupon;
  const customer = details?.customer;
  const card = details?.collectorCard;
  const transactions = card?.transactionIds?.length ? card.transactionIds : (coupon?.linkedTransactionIds ?? []);

  return (
    <div style={ styles.page }>
      <TopNavBar handleUpdate={ handleUpdate } activeRoute='/coupons/lookup' />
      <SideNavBar handleUpdate={ handleUpdate } activeRoute='/coupons/lookup' />
      <main style={{ ...styles.main, marginLeft: isMobile ? MOBILE_CONTENT_MARGIN_LEFT : DESKTOP_CONTENT_MARGIN_LEFT }}>
        <nav aria-label='Breadcrumb' style={ styles.breadcrumb }>
          <button type='button' onClick={ () => navigate('/coupons') } style={ styles.textButton }>Coupons</button>
          <span>/</span>
          <button type='button' onClick={ () => navigate('/coupons/lookup') } style={ styles.textButton }>Lookup</button>
          <span>/</span>
          <span>{ redemptionId }</span>
        </nav>

        { isLoading ? <p style={ styles.message }>Loading coupon...</p> : null }
        { error ? <div role='alert' style={ styles.error }>{ error }</div> : null }

        { coupon ? (
          <>
            <div style={ styles.header }>
              <div>
                <div style={ styles.eyebrow }>COUPON DETAILS</div>
                <h1 style={ styles.title }>{ coupon.redemptionId }</h1>
                <p style={ styles.subtitle }>{ coupon.couponType ?? 'Collector-card reward' }</p>
              </div>
              <span style={{ ...styles.status, ...(coupon.isRedeemedAndClosed ? styles.redeemedStatus : styles.unredeemedStatus) }}>
                { coupon.isRedeemedAndClosed ? 'Redeemed' : 'Not redeemed' }
              </span>
            </div>

            <div style={ styles.grid }>
              <section style={ styles.panel }>
                <h2 style={ styles.sectionTitle }>Coupon</h2>
                <div style={ styles.fields }>
                  <DetailField label='Created On' value={ formatDate(coupon.createdOn, coupon.createdOnReadable) } />
                  <DetailField label='Is Redeemed?' value={ coupon.isRedeemedAndClosed ? 'Yes' : 'No' } />
                  { coupon.isRedeemedAndClosed ? (
                    <>
                      <DetailField label='Redeemed On' value={ formatDate(coupon.redeemedAndClosedOn, coupon.redeemedAndClosedOnReadable) } />
                      <DetailField label='Redeemed Store (code)' value={ coupon.redeemedStoreCode } />
                      <DetailField label='Store Transaction Reference' value={ coupon.clientTransactionReference } />
                    </>
                  ) : null }
                </div>
              </section>

              <section style={ styles.panel }>
                <h2 style={ styles.sectionTitle }>Loyalty Customer</h2>
                <div style={ styles.fields }>
                  <DetailField label='Name' value={ [customer?.firstName, customer?.surname].filter(Boolean).join(' ') } />
                  <DetailField label='Account ID' value={ customer?.accountId } />
                  <DetailField label='Email' value={ customer?.email } />
                  <DetailField label='Mobile' value={ customer?.mobile } />
                  <DetailField label='Address' value={ [customer?.address, customer?.postCode].filter(Boolean).join(', ') } />
                  <DetailField label='County' value={ customer?.county } />
                </div>
                { customer?.accountId ? (
                  <button type='button' style={ styles.userLink } onClick={ () => navigate('/users-users/' + encodeURIComponent(customer.accountId)) }>
                    View customer details
                  </button>
                ) : null }
              </section>
            </div>

            <section style={{ ...styles.panel, marginTop: 18 }}>
              <h2 style={ styles.sectionTitle }>Stamp Collection History</h2>
              <p style={ styles.chartNote }>Stamps earned by transaction date</p>
              { historyLoading ? <p style={ styles.message }>Loading collection history...</p> : null }
              { historyError ? <p role='alert' style={ styles.historyError }>{ historyError }</p> : null }
              { !historyLoading && !historyError && collectionHistory.length === 0 ? (
                <p style={ styles.message }>No linked purchase dates were found.</p>
              ) : null }
              { !historyLoading && !historyError && collectionHistory.length > 0 ? (
                <div style={ styles.chartWrapper }>
                  <ResponsiveContainer width='100%' height='100%'>
                    <BarChart data={ collectionHistory } margin={{ top: 16, right: 18, left: 4, bottom: 8 }}>
                      <CartesianGrid stroke='#e5edf2' strokeDasharray='4 4' vertical={false} />
                      <XAxis dataKey='date' tickFormatter={ (value) => dayjs(value).format('DD/MMM/YY') } tick={{ fontSize: 11 }} minTickGap={18} />
                      <YAxis allowDecimals={false} width={42} tick={{ fontSize: 12 }} />
                      <Tooltip labelFormatter={ (value) => dayjs(value).format('DD/MMM/YYYY') } />
                      <Bar dataKey='transactions' name='Stamps collected' fill='#009fe3' radius={[4, 4, 0, 0]} maxBarSize={36} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : null }
              { !historyLoading && !historyError && missingTransactionCount > 0 ? (
                <p style={ styles.chartNote }>{ missingTransactionCount } stamp(s) had no matching purchase date.</p>
              ) : null }
            </section>

            <section style={{ ...styles.panel, marginTop: 18 }}>
              <h2 style={ styles.sectionTitle }>Closed Collector Card</h2>
              { card ? (
                <div style={ styles.fields }>
                  <DetailField label='Collector Card Type' value={ card.type } />
                  <DetailField label='Collector Card ID' value={ card.id } />
                  <DetailField label='Started On' value={ formatDate(card.createdOn, card.createdOnReadable) } />
                  <DetailField label='Completed On' value={ formatDate(card.closedOn, card.closedOnReadable) } />
                </div>
              ) : <p style={ styles.message }>No matching closed collector card was found on the customer record.</p> }
              <div style={ styles.transactions }>
                <h3 style={ styles.transactionTitle }>Linked Transaction IDs ({ transactions.length })</h3>
                { transactions.length > 0 ? (
                  <div style={ styles.transactionList }>
                    { transactions.map((transactionId) => <span key={ transactionId } style={ styles.transactionPill }>{ transactionId }</span>) }
                  </div>
                ) : <p style={ styles.message }>No linked transactions recorded.</p> }
              </div>
            </section>
          </>
        ) : null }
      </main>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#f6f9fb' },
  main: { padding: '72px 22px 32px', minWidth: 0 },
  breadcrumb: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, color: '#697b85', fontSize: 13, marginBottom: 20, overflowWrap: 'anywhere' },
  textButton: { background: 'none', border: 0, padding: 0, color: '#087f8c', cursor: 'pointer', fontSize: 13 },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  eyebrow: { color: '#087f8c', fontSize: 12, fontWeight: 800, letterSpacing: 1.2, marginBottom: 6 },
  title: { color: '#153243', fontSize: 28, fontWeight: 800, margin: 0, overflowWrap: 'anywhere' },
  subtitle: { color: '#637782', margin: '6px 0 0' },
  status: { borderRadius: 999, padding: '8px 13px', fontSize: 13, fontWeight: 800 },
  redeemedStatus: { color: '#176d58', backgroundColor: '#dff7eb' },
  unredeemedStatus: { color: '#9c6400', backgroundColor: '#fff1cf' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 18 },
  panel: { backgroundColor: 'white', border: '1px solid #dbe7ed', borderRadius: 10, padding: '20px 22px', minWidth: 0 },
  sectionTitle: { color: '#153243', fontSize: 19, fontWeight: 800, margin: '0 0 18px' },
  fields: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))', gap: 18 },
  detailField: { display: 'grid', gap: 4, minWidth: 0 },
  fieldLabel: { color: '#697b85', fontSize: 12 },
  fieldValue: { color: '#203744', fontSize: 15, fontWeight: 700, overflowWrap: 'anywhere' },
  userLink: { background: 'none', border: 0, padding: 0, color: '#087f8c', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 20 },
  transactions: { marginTop: 22, borderTop: '1px solid #e4edf1', paddingTop: 18 },
  transactionTitle: { color: '#314955', fontSize: 15, fontWeight: 800, margin: '0 0 12px' },
  transactionList: { display: 'flex', flexWrap: 'wrap', gap: 7 },
  transactionPill: { border: '1px solid #dbe5ed', borderRadius: 6, backgroundColor: '#f8fbfd', color: '#495057', padding: '5px 8px', fontSize: 12, fontFamily: 'monospace' },
  chartWrapper: { width: '100%', height: 300 },
  chartNote: { color: '#697b85', fontSize: 13, margin: '0 0 14px' },
  historyError: { color: '#9d3434', fontSize: 14, margin: 0 },
  message: { color: '#697b85', fontSize: 14, margin: 0 },
  error: { backgroundColor: '#fff5f5', border: '1px solid #efc8c8', borderRadius: 8, color: '#9d3434', padding: 14 }
};

export default CouponDetails;
